import React, { useContext, useEffect, useState } from 'react';
import FindRestaurant from './../components/FindRestaurant';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import MenuBar from './../components/Menubar';
import LoaderContext from '../components/LoaderContext';
import { getProfileImageUrl } from '../utils/profileImage';

type Props = {
  location: any;
  user: any;
  handleLocationChange(obj: {name:string; lat:number; lng:number}): void;
};

type SearchResponseDTO = {
  id: number;
  type: 'USER' | 'YOLO' | 'RESTAURANT';
  restaurantId: number;
  title: string;
  subtitle?: string;
  imageUrl?: string;
  order: number;
}

const getSearchResultPath = (item: SearchResponseDTO): string => {
  switch (item.type) {
    case 'USER':
      return `/user/${item.id}`;

    case 'RESTAURANT':
      return `/restaurant/${item.id}`;

    case 'YOLO':
      if (!item.restaurantId) return '/';
      return `/restaurant/${item.restaurantId}`;

    default:
      return '/';
  }
};

const getAvatarUrl = (item: SearchResponseDTO): string | null => {
  if (item.type === 'USER')
    return getProfileImageUrl(item.imageUrl);

  if (item.type === 'RESTAURANT') {
    return `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
      item.title
    )}`;
  }

  if (item.type === 'YOLO') {
    if (item.imageUrl)
      return getProfileImageUrl(item.imageUrl);

    return `https://api.dicebear.com/7.x/initials/svg?seed=Y`;
  }

  return null;
};

async function fetchRestaurantId(restaurantName) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/restaurants?name=${encodeURIComponent(restaurantName)}`
    );
    return { data: response.data, exists: true };
  } catch (error) {
    return { exists: false };
  }
}

const getStoredLocation = () => {
  try {
    const raw = localStorage.getItem('yolo.location');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const RestaurantSearch: React.FC<Props> = (props) => {
  const [place, setPlace] = useState({ name: '', address: '', latitude: '', longitude: '' });
  const [error, setError] = useState('');
  const history = useHistory();
  const { showLoader, hideLoader } = useContext(LoaderContext);

  const [globalQuery, setGlobalQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResponseDTO[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (globalQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setError("");
        setIsSearching(true);
        const location = getStoredLocation();

        if (!location?.lat || !location?.lng) {
          setSearchResults([]);
          return;
        }

        const res = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/search/everything`,
          {
            params: {
              query: globalQuery,
              lat: location.lat,
              lng: location.lng
            }
          }
        );
        setSearchResults(res.data || []);
      } catch (e) {
        console.error(e);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [globalQuery]);


  const handlePlaceChanged = async (place) => {
    setError('');
    setPlace(place);

    const result = await fetchRestaurantId(place.name);
    if (result) {
      if (result.exists) {
        history.push(`/restaurant/${result.data}`);
        hideLoader();
      } else {
        setError("We're sorry, but there are no favorites saved at this restaurant yet.");
        hideLoader();
      }
    } else {
      setError('Error fetching restaurant information. Please try again.');
      hideLoader();
    }
  };

  const handleCityChanged = (loc) => {
    props.handleLocationChange(loc);
    setSearchResults([]);
    setGlobalQuery('');
  };

  const handlePlaceError = (error) => {
    setError(error);
    hideLoader();
  };

  const users = searchResults.filter(r => r.type === 'USER');
  const restaurants = searchResults.filter(r => r.type === 'RESTAURANT');
  const yolos = searchResults.filter(r => r.type === 'YOLO');
  const showSearchLoading = isSearching && searchResults.length === 0 && globalQuery.trim().length >= 2;

  const renderSearchCard = (item: SearchResponseDTO) => (
    <div
      key={`${item.type}-${item.id}`}
      className="search-card"
      onClick={() => history.push(getSearchResultPath(item))}
    >
      <div className="search-card-avatar">
        <img src={getAvatarUrl(item) || ''} alt={item.title} loading="lazy" />
      </div>
      <div className="search-card-content">
        <div className="search-card-title">{item.title}</div>
        {item.type !== 'USER' && item.subtitle && (
          <div className="search-card-subtitle">{item.subtitle}</div>
        )}
      </div>
    </div>
  );

  return (
    <main>
      <div className="container">
        <MenuBar user={props.user} />
        <section>
          <div className="small-container text-center">
            <FindRestaurant
              user={props.user}
              location={props.location}
              handlePlaceChanged={handlePlaceChanged}
              handlePlaceError={handlePlaceError}
              onCityChanged={handleCityChanged}
            />
            <div className="global-search-wrapper">
              <input
                type="text"
                className="field xl-filed location-input text-center"
                placeholder="TYPE HERE FOR ADVANCED SEARCH"
                value={globalQuery}
                style={{letterSpacing: '0.5px'}}
                onChange={(e) => setGlobalQuery(e.target.value)}
              />
            </div>
            {showSearchLoading && (
              <div className="search-results search-loading mt-3" aria-live="polite" role="status">
                <div className="search-loading-row">
                  <span className="search-loading-dot" />
                  <span className="search-loading-label">Searching</span>
                </div>
                <div className="search-loading-bar" />
              </div>
            )}
            {searchResults.length > 0 && (
              <div className="search-results mt-3">
                <h4 className="search-results-title">Search results</h4>
                <div className="search-results-list">
                  <div className="search-results-list">
                    {users.map(renderSearchCard)}
                    {restaurants.map(renderSearchCard)}
                    {yolos.map(renderSearchCard)}
                  </div>
                </div>
              </div>
            )}
            {error && (
              <h3 className="futuralt-book mt-5 pt-1 mb-2" style={{ fontSize: '22px' }}>
                {' '}
                {error}{' '}
              </h3>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default RestaurantSearch;
